import React, { useMemo, useRef, useState } from 'react';

import { Link } from 'react-router-dom';

import { Button } from '../UI/Button';

import { AgentPlatformPanel } from './AgentPlatformPanel';

import { agentPlatformService } from '../../services/agentPlatformService';

import type { AgentExecutionDto, AgentExecutionStatus } from '../../types/agentPlatform';

import { labelExecutionStatus } from '../../utils/agentPlatformLabels';

import { apTheme } from '../../utils/agentPlatformTheme';



export type PlaygroundMessage = {

  id: string;

  role: 'user' | 'assistant' | 'error';

  content: string;

  executionId?: string;

  tokens?: number;

  status?: string;

  at: string;

};



type Props = {

  agentId: string;

};



function newSessionId(): string {

  if (typeof crypto !== 'undefined' && crypto.randomUUID) {

    return crypto.randomUUID();

  }

  return `session-${Date.now()}`;

}



function formatOutput(output: string | null | undefined): string {

  if (!output) return '(sem resposta)';

  try {

    const parsed = JSON.parse(output);

    if (typeof parsed === 'string') return parsed;

    if (parsed?.message) return String(parsed.message);

    if (parsed?.text) return String(parsed.text);

    if (parsed?.content) return String(parsed.content);

    return JSON.stringify(parsed, null, 2);

  } catch {

    return output;

  }

}



export const AgentPlaygroundPanel: React.FC<Props> = ({ agentId }) => {

  const [sessionId, setSessionId] = useState(newSessionId);

  const [messages, setMessages] = useState<PlaygroundMessage[]>([]);

  const [draft, setDraft] = useState('');

  const [advancedJson, setAdvancedJson] = useState(false);

  const [jsonInput, setJsonInput] = useState('{"message":""}');

  const [loading, setLoading] = useState(false);

  const [lastExecution, setLastExecution] = useState<AgentExecutionDto | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);



  const resetSession = () => {

    setSessionId(newSessionId());

    setMessages([]);

    setLastExecution(null);

    setDraft('');

    setJsonInput('{"message":""}');

  };



  const appendMessage = (msg: PlaygroundMessage) => {

    setMessages(prev => [...prev, msg]);

    requestAnimationFrame(() => {

      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });

    });

  };



  const send = async () => {

    const inputPayload = advancedJson

      ? jsonInput

      : JSON.stringify({ message: draft.trim() });



    if (!advancedJson && !draft.trim()) return;



    const userText = advancedJson ? jsonInput : draft.trim();

    appendMessage({

      id: `u-${Date.now()}`,

      role: 'user',

      content: userText,

      at: new Date().toISOString(),

    });

    setDraft('');

    setLoading(true);



    try {

      const result = await agentPlatformService.executeAgent(agentId, {

        input: inputPayload,

        testMode: true,

        sessionId,

      });

      setLastExecution(result);

      const assistantText = formatOutput(result.output);

      appendMessage({

        id: result.id,

        role: result.status === 'Failed' ? 'error' : 'assistant',

        content: result.errorMessage ?? assistantText,

        executionId: result.id,

        tokens: result.totalTokens,

        status: result.status,

        at: new Date().toISOString(),

      });

    } catch {

      appendMessage({

        id: `err-${Date.now()}`,

        role: 'error',

        content: 'Falha na execução de teste.',

        at: new Date().toISOString(),

      });

    } finally {

      setLoading(false);

    }

  };



  const sessionLabel = useMemo(() => sessionId.slice(0, 8), [sessionId]);



  return (

    <div className="grid lg:grid-cols-3 gap-6">

      <div className={`lg:col-span-2 ${apTheme.chat.container}`}>

        <div className="px-5 py-4 border-b border-slate-200/80 flex flex-wrap items-center justify-between gap-2 bg-white/60 rounded-t-2xl">

          <div>

            <h2 className="font-semibold text-slate-900">Playground</h2>

            <p className="text-xs text-slate-500 font-mono">session · {sessionLabel}…</p>

          </div>

          <div className="flex gap-2">

            <Button variant="ghost" size="sm" onClick={() => setAdvancedJson(v => !v)}>

              {advancedJson ? 'Modo chat' : 'JSON avançado'}

            </Button>

            <Button variant="secondary" size="sm" onClick={resetSession}>

              Nova sessão

            </Button>

          </div>

        </div>



        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">

          {messages.length === 0 && (

            <div className="text-center py-12">

              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600 mb-3">

                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />

                </svg>

              </div>

              <p className="text-sm text-slate-500 max-w-sm mx-auto">

                Envie uma mensagem para testar o agente. O sessionId correlaciona múltiplos turnos.

              </p>

            </div>

          )}

          {messages.map(msg => (

            <div

              key={msg.id}

              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${

                msg.role === 'user'

                  ? apTheme.chat.user

                  : msg.role === 'error'

                    ? apTheme.chat.error

                    : apTheme.chat.assistant

              }`}

            >

              <pre className="whitespace-pre-wrap font-sans">{msg.content}</pre>

              {msg.role !== 'user' && msg.executionId && (

                <div className="mt-2 text-xs opacity-80 flex flex-wrap gap-2">

                  {msg.status && (

                    <span>{labelExecutionStatus(msg.status as AgentExecutionStatus)}</span>

                  )}

                  {msg.tokens != null && <span>{msg.tokens} tokens</span>}

                  <Link

                    to={`/agent-platform/executions/${msg.executionId}`}

                    className="underline text-red-600"

                  >

                    detalhe

                  </Link>

                </div>

              )}

            </div>

          ))}

          {loading && (

            <div className="flex items-center gap-2 text-sm text-slate-500">

              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />

              Agente pensando…

            </div>

          )}

        </div>



        <div className="p-4 border-t border-slate-200/80 bg-white/80 rounded-b-2xl">

          {advancedJson ? (

            <textarea

              className={`${apTheme.input} font-mono mb-3`}

              rows={4}

              value={jsonInput}

              onChange={e => setJsonInput(e.target.value)}

            />

          ) : (

            <textarea

              className={`${apTheme.input} mb-3 resize-none`}

              rows={2}

              placeholder="Digite sua mensagem…"

              value={draft}

              onChange={e => setDraft(e.target.value)}

              onKeyDown={e => {

                if (e.key === 'Enter' && !e.shiftKey) {

                  e.preventDefault();

                  if (!loading) send();

                }

              }}

            />

          )}

          <Button onClick={send} disabled={loading}>

            {loading ? 'Executando…' : 'Enviar'}

          </Button>

        </div>

      </div>



      <AgentPlatformPanel title="Última execução" description="TestMode — rascunhos permitidos.">

        {!lastExecution && <p className="text-sm text-slate-500">Nenhuma execução nesta sessão.</p>}

        {lastExecution && (

          <>

            <dl className="space-y-2 text-sm">

              <div className="flex justify-between">

                <dt className="text-slate-500">Status</dt>

                <dd className="font-medium">{labelExecutionStatus(lastExecution.status)}</dd>

              </div>

              <div className="flex justify-between">

                <dt className="text-slate-500">Tokens</dt>

                <dd className="font-medium">{lastExecution.totalTokens}</dd>

              </div>

              <div className="flex justify-between gap-2">

                <dt className="text-slate-500 shrink-0">Correlation</dt>

                <dd className="font-mono text-xs truncate text-slate-700">{lastExecution.correlationId}</dd>

              </div>

            </dl>

            <Link

              to={`/agent-platform/executions/${lastExecution.id}`}

              className="text-red-600 hover:text-red-800 text-sm inline-block mt-3 font-medium"

            >

              Abrir timeline completa →

            </Link>

          </>

        )}

      </AgentPlatformPanel>

    </div>

  );

};


